import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { admin } from "../../lib/api";
import { inr } from "../../lib/format";
import { panel } from "../ui";
import { IndianRupee, ShoppingBag, Users, TrendingUp, AlertTriangle } from "lucide-react";

function Stat({ icon: Icon, label, value, sub }) {
  return (
    <div className={`${panel} p-5`}>
      <div className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-widest text-zinc-500">{label}</span>
        <Icon size={16} className="text-accent" />
      </div>
      <p className="font-display text-4xl text-white mt-3">{value}</p>
      {sub && <p className="text-xs text-zinc-500 mt-1">{sub}</p>}
    </div>
  );
}

export default function Overview() {
  const [d, setD] = useState(null);
  useEffect(() => { admin.get("/admin/analytics/overview").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <p className="text-zinc-500">Loading…</p>;

  const maxF = Math.max(...d.funnel.map((f) => f.count), 1);

  return (
    <div>
      <h1 className="font-display text-3xl sm:text-4xl text-white uppercase tracking-wide mb-6">Overview</h1>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
        <Stat icon={IndianRupee} label="Revenue" value={inr(d.revenue)} sub={`${inr(d.revenue_week)} this week`} />
        <Stat icon={ShoppingBag} label="Orders" value={d.orders} sub={`${d.pending_count} pending`} />
        <Stat icon={Users} label="Visitors" value={d.visitors} sub={`${d.sessions} sessions`} />
        <Stat icon={TrendingUp} label="Avg Order" value={inr(d.aov)} sub={`${d.conversion}% conv.`} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className={`${panel} p-5 lg:col-span-2`}>
          <h2 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">Recent Orders</h2>
          {d.recent_orders.length === 0 ? <p className="text-sm text-zinc-500">No orders yet.</p> : (
            <div className="space-y-2">
              {d.recent_orders.map((o) => (
                <Link key={o.id} to={`/admin/orders/${o.id}`} className="flex items-center justify-between py-2 border-b border-zinc-900 hover:bg-zinc-900/40 px-2">
                  <div><span className="text-sm text-white font-medium">{o.order_number}</span><span className="text-xs text-zinc-500 ml-2">{o.contact?.email}</span></div>
                  <div className="flex items-center gap-3"><span className="text-xs text-zinc-400">{o.status}</span><span className="text-sm text-white">{inr(o.total)}</span></div>
                </Link>
              ))}
            </div>
          )}
        </div>

        <div className={`${panel} p-5`}>
          <h2 className="text-sm font-semibold text-white uppercase tracking-wider mb-4 flex items-center gap-2"><AlertTriangle size={15} className="text-amber-400" /> Low Stock</h2>
          {d.low_stock.length === 0 ? <p className="text-sm text-zinc-500">All stocked up.</p> : (
            <div className="space-y-2">
              {d.low_stock.map((p) => (
                <div key={p.id} className="flex justify-between text-sm"><span className="text-zinc-300 line-clamp-1">{p.name}</span><span className={p.stock === 0 ? "text-red-400" : "text-amber-400"}>{p.stock}</span></div>
              ))}
            </div>
          )}
        </div>

        <div className={`${panel} p-5`}>
          <h2 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">Conversion Funnel</h2>
          <div className="space-y-2">
            {d.funnel.map((f) => (
              <div key={f.step}>
                <div className="flex justify-between text-xs text-zinc-400 mb-1"><span className="capitalize">{f.step.replace("_", " ")}</span><span>{f.count}</span></div>
                <div className="h-2 bg-zinc-900"><div className="h-full" style={{ width: `${(f.count / maxF) * 100}%`, background: "var(--ss-accent)" }} /></div>
              </div>
            ))}
          </div>
        </div>

        <div className={`${panel} p-5 lg:col-span-2`}>
          <h2 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">Top Products</h2>
          <div className="space-y-2">
            {d.top_products.map((p) => (
              <div key={p.id} className="flex justify-between text-sm"><span className="text-zinc-300 line-clamp-1">{p.name}</span><span className="text-zinc-500">{p.sold} sold</span></div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
